import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";
import { CREAM } from "@/components/BatchWorkbench/styles/theme";
import { useSimulatedLoadingPercent } from "@/hooks/useLoadingProgressPercent";
import { cn } from "@/lib/utils";
import "./DatasetStudioPageSkeleton.css";

const PAGE_CONTAINER = "mx-auto w-full max-w-[1800px] px-6";

const skeletonTone = {
  strong: "var(--studio-skeleton-strong, rgba(31,39,83,0.08))",
  medium: "var(--studio-skeleton-medium, rgba(31,39,83,0.06))",
  soft: "var(--studio-skeleton-soft, rgba(31,39,83,0.05))",
  panel: "var(--studio-skeleton-panel, rgba(31,39,83,0.07))",
} as const;
const skeletonCardBorder = "0.5px solid var(--studio-skeleton-border, rgba(31,39,83,0.10))";

function LoadingProgressBar({
  percent,
  label = "Loading",
  fading = false,
  busy = true,
}: {
  percent: number;
  label?: string;
  fading?: boolean;
  busy?: boolean;
}) {
  const clamped = Math.min(100, Math.max(0, Math.round(percent)));

  return (
    <>
      <div
        className={cn(
          "pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px] overflow-hidden transition-opacity duration-200 motion-reduce:transition-none md:hidden",
          fading ? "opacity-0" : "opacity-100",
        )}
        style={{ backgroundColor: skeletonTone.soft }}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        aria-hidden={fading}
      >
        <div
          className="h-full transition-[width] duration-200 ease-out motion-reduce:transition-none"
          style={{ width: `${clamped}%`, backgroundColor: CREAM.accent }}
        />
      </div>
      <div
        className={cn(
          "pointer-events-none fixed bottom-20 right-4 z-50 hidden w-40 rounded-lg border px-3 py-2 shadow-sm transition-opacity duration-200 motion-reduce:transition-none md:block lg:bottom-4",
          fading ? "opacity-0" : "opacity-100",
        )}
        style={{ background: CREAM.card, borderColor: CREAM.borderStrong }}
        aria-live="polite"
        role="status"
        aria-busy={busy}
        aria-hidden={fading}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2 text-xs leading-none">
          <span className="truncate" style={{ color: CREAM.mute }}>{label}</span>
          <span className="shrink-0 tabular-nums font-semibold" style={{ color: CREAM.ink }}>{clamped}%</span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full" style={{ backgroundColor: skeletonTone.soft }}>
          <div
            className="h-full rounded-full transition-[width] duration-200 ease-out motion-reduce:transition-none"
            style={{ width: `${clamped}%`, backgroundColor: CREAM.accent }}
          />
        </div>
      </div>
    </>
  );
}

export function DatasetStudioBootstrapProgress({
  loading, progress, cacheHit, cacheExpected,
}: {
  loading: boolean;
  progress: number;
  cacheHit: boolean;
  cacheExpected: boolean;
}) {
  const wasLoading = useRef(loading);
  const [completion, setCompletion] = useState<75 | 100 | "fading" | null>(null);
  // Start moving during token/cache retrieval, not only after the response.
  // This is visual progress, not bytes received; it cannot finish the load.
  const waitingProgress = useSimulatedLoadingPercent(loading && cacheExpected, 75);

  useEffect(() => {
    if (loading) {
      wasLoading.current = true;
      setCompletion(null);
      return;
    }
    const completed = wasLoading.current;
    wasLoading.current = false;
    if (!completed || !cacheHit) {
      setCompletion(null);
      return;
    }
    // Cosmetic cache-hit completion only. The dataset renders immediately;
    // this independently mounted indicator finishes over the ready content.
    setCompletion(75);
    const fill = window.setTimeout(() => setCompletion(100), 450);
    const fade = window.setTimeout(() => setCompletion("fading"), 950);
    const hide = window.setTimeout(() => setCompletion(null), 1150);
    return () => {
      window.clearTimeout(fill);
      window.clearTimeout(fade);
      window.clearTimeout(hide);
    };
  }, [loading, cacheHit]);

  // Keep the same bar and its waiting progress across the response render,
  // then complete over the content without holding back the dataset.
  const startingCompletion = !loading && wasLoading.current && cacheHit;
  if (!loading && completion === null && !startingCompletion) return null;
  return (
    <LoadingProgressBar
      percent={loading ? Math.max(progress, waitingProgress) : completion === null ? waitingProgress : completion === 75 ? 75 : 100}
      label={loading ? "Loading dataset" : "Dataset ready"}
      busy={loading}
      fading={!loading && completion === "fading"}
    />
  );
}

export function StudioDatasetTableSkeleton({ count = 12 }: { count?: number }) {
  const isPhone = useIsMobile();
  if (isPhone) return (
    <div className="ds-studio-skeleton grid min-w-0 grid-cols-1 gap-2" role="status" aria-label="Loading datasets" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex min-w-0 gap-3 rounded-xl border p-2" style={{ borderColor: "rgba(31,39,83,0.08)" }} aria-hidden="true">
          <Skeleton className="studio-preview-placeholder min-h-[84px] w-[min(42%,200px)] shrink-0 rounded-lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Skeleton className="h-4 w-20 rounded" style={{ backgroundColor: skeletonTone.medium }} />
            <Skeleton className="h-3 w-full rounded" style={{ backgroundColor: skeletonTone.strong }} />
            <Skeleton className="h-3 w-2/3 rounded" style={{ backgroundColor: skeletonTone.strong }} />
            <Skeleton className="mt-auto h-7 w-full rounded-lg" style={{ backgroundColor: skeletonTone.panel }} />
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <div className="ds-studio-skeleton overflow-x-auto rounded-xl border" style={{ borderColor: "rgba(31,39,83,0.08)" }} role="status" aria-label="Loading datasets" aria-busy="true">
      <table className="w-full border-collapse text-left" aria-hidden="true">
        <thead style={{ background: CREAM.panel, color: CREAM.mute }}>
          <tr>{["Preview", "Publisher", "Dataset", "Description", "Actions"].map(label => (
            <th key={label} className="px-4 py-3 text-xs font-medium last:text-right">{label}</th>
          ))}</tr>
        </thead>
        <tbody className="divide-y divide-[rgba(31,39,83,0.06)]">
          {Array.from({ length: count }).map((_, index) => (
            <tr key={index}>
              <td className="w-32 px-4 py-1.5"><Skeleton className="studio-preview-placeholder h-[54px] w-24 rounded-lg" /></td>
              <td className="w-32 min-w-[128px] px-4 py-1.5"><Skeleton className="h-4 w-20 rounded" style={{ backgroundColor: skeletonTone.medium }} /></td>
              <td className="min-w-[200px] px-4 py-1.5"><div className="space-y-2"><Skeleton className={`h-3 rounded ${index % 2 ? "w-3/4" : "w-5/6"}`} style={{ backgroundColor: skeletonTone.strong }} /><Skeleton className="h-2.5 w-1/2 rounded" style={{ backgroundColor: skeletonTone.soft }} /></div></td>
              <td className="min-w-[260px] px-4 py-1.5"><div className="space-y-2">
                <Skeleton className="h-3 w-full rounded" style={{ backgroundColor: skeletonTone.soft }} />
                <Skeleton className={`h-3 rounded ${index % 2 ? "w-2/3" : "w-4/5"}`} style={{ backgroundColor: skeletonTone.soft }} />
              </div></td>
              <td className="px-4 py-1.5"><Skeleton className="ml-auto h-8 w-32 rounded-lg" style={{ backgroundColor: skeletonTone.panel }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SidebarNavSkeleton() {
  return (
    <div
      className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-16 lg:flex-col border-r bg-[#FCFAF4]"
      style={{ borderColor: "var(--studio-skeleton-border, #e2dccd)" }}
      aria-hidden="true"
    >
      <div className="flex h-12 shrink-0 items-center justify-center">
        <Skeleton className="h-6 w-6 rounded-md" style={{ backgroundColor: skeletonTone.medium }} />
      </div>
      <div className="flex flex-1 flex-col items-center gap-2 px-2 pt-4">
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-9 w-9 rounded-[10px]"
            style={{ backgroundColor: index === 3 ? skeletonTone.strong : skeletonTone.soft }}
          />
        ))}
      </div>
      <div className="shrink-0 border-t p-2" style={{ borderColor: "var(--studio-skeleton-border, #e2dccd)" }}>
        <Skeleton className="mx-auto h-7 w-7 rounded-full" style={{ backgroundColor: skeletonTone.panel }} />
      </div>
    </div>
  );
}

function MobileTopBarSkeleton() {
  return (
    <div
      className="sticky top-0 z-40 flex h-12 shrink-0 items-center gap-2 bg-[#FCFAF4] px-3 lg:hidden"
      aria-hidden="true"
    >
      <Skeleton className="h-8 w-8 rounded-lg" style={{ backgroundColor: skeletonTone.medium }} />
      <Skeleton className="h-4 w-24 rounded" style={{ backgroundColor: skeletonTone.strong }} />
    </div>
  );
}

function ListingHeaderSkeleton() {
  return (
    <div
      className="hidden flex-shrink-0 border-b md:block"
      style={{ borderColor: `var(--studio-skeleton-border, ${CREAM.borderStrong})`, background: CREAM.nav }}
    >
      <div className={cn("flex h-16 items-center gap-3", PAGE_CONTAINER)}>
        <Skeleton className="hidden h-4 w-24 rounded md:block" style={{ backgroundColor: skeletonTone.medium }} />
        <Skeleton className="hidden h-4 w-4 rounded md:block" style={{ backgroundColor: skeletonTone.soft }} />
        <Skeleton className="hidden h-4 w-10 rounded md:block" style={{ backgroundColor: skeletonTone.strong }} />
        <div className="ml-auto flex min-w-0 items-center gap-3">
          <Skeleton className="h-8 w-28 rounded-md" style={{ backgroundColor: skeletonTone.panel }} />
        </div>
      </div>
    </div>
  );
}

function DetailHeaderSkeleton() {
  return (
    <div
      className="flex-shrink-0 border-b"
      style={{ borderColor: `var(--studio-skeleton-border, ${CREAM.borderStrong})`, background: CREAM.bg }}
    >
      <div className="hidden h-12 min-w-0 items-center gap-2 px-2 lg:flex">
        <Skeleton className="size-7 shrink-0 rounded-[8px]" style={{ backgroundColor: skeletonTone.medium }} />
        <Skeleton className="h-4 w-24 rounded" style={{ backgroundColor: skeletonTone.medium }} />
        <Skeleton className="h-4 w-4 rounded" style={{ backgroundColor: skeletonTone.soft }} />
        <Skeleton className="h-4 w-16 rounded" style={{ backgroundColor: skeletonTone.strong }} />
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Skeleton className="h-8 w-52 rounded-[10px]" style={{ backgroundColor: skeletonTone.medium }} />
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-2 px-3 py-2 lg:hidden">
        <Skeleton className="size-10 shrink-0 rounded-[8px]" style={{ backgroundColor: skeletonTone.medium }} />
        <Skeleton className="h-4 w-28 rounded" style={{ backgroundColor: skeletonTone.strong }} />
      </div>
    </div>
  );
}

function DetailLeftRailSkeleton() {
  return (
    <aside
      className="ds-studio-detail-skeleton-rail hidden h-full min-h-0 shrink-0 flex-col gap-3 overflow-hidden p-3 md:flex"
      style={{
        borderRight: `1px solid var(--studio-skeleton-border, ${CREAM.borderSoft})`,
        background: CREAM.card,
      }}
      aria-hidden="true"
    >
      <Skeleton className="h-8 w-full rounded-md" style={{ backgroundColor: skeletonTone.medium }} />
      <Skeleton className="h-7 w-full rounded-md" style={{ backgroundColor: skeletonTone.panel }} />
      <div className="flex flex-col gap-2 pt-1">
        {Array.from({ length: 10 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-8 w-full rounded-md"
            style={{ backgroundColor: index % 3 === 0 ? skeletonTone.medium : skeletonTone.soft }}
          />
        ))}
      </div>
    </aside>
  );
}

function DetailBodySkeleton() {
  return (
    <div className="ds-studio-detail-skeleton-body flex h-full min-h-0 w-full flex-col overflow-hidden md:flex-row">
      <div
        className="flex flex-col gap-2 border-b p-3 md:hidden"
        style={{ borderColor: CREAM.borderSoft }}
        aria-hidden="true"
      >
        <Skeleton className="h-11 w-full rounded-full" style={{ backgroundColor: skeletonTone.medium }} />
        <Skeleton className="h-11 w-full rounded-lg" style={{ backgroundColor: skeletonTone.panel }} />
      </div>
      <DetailLeftRailSkeleton />
      <div className="min-w-0 flex-1 overflow-y-auto px-3 pb-2 max-md:pb-64 md:max-lg:pb-28">
        <WorkbenchClipsGridSkeleton showProgress={false} />
      </div>
    </div>
  );
}

function WorkbenchClipCardSkeleton({ cardSize }: { cardSize?: "list" | "small" | "medium" | "large" }) {
  const responsiveList = cardSize === undefined;
  const isList = cardSize === "list";
  return (
    <div
      className={cn("flex h-full overflow-hidden rounded-md", isList ? "flex-row items-stretch" : "flex-col", responsiveList && "max-md:flex-row max-md:items-stretch")}
      style={{ background: CREAM.card, border: skeletonCardBorder }}
    >
      <div className={cn("relative shrink-0", isList ? "w-[40%] sm:w-[200px]" : "w-full", responsiveList && "max-md:w-[40%] sm:max-md:w-[200px]")}
        style={{ aspectRatio: "16 / 9", backgroundColor: skeletonTone.medium }}>
        <div className="absolute bottom-1.5 left-1.5 flex gap-1">
          <Skeleton className="h-4 w-16 rounded" style={{ backgroundColor: skeletonTone.panel }} />
          <Skeleton className="h-4 w-12 rounded max-md:hidden" style={{ backgroundColor: skeletonTone.panel }} />
        </div>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col px-2 py-1.5">
        <div className="min-w-0 shrink-0 space-y-1 pb-1.5" style={{ minHeight: cardSize === "small" ? 40 : 36 }}>
          <Skeleton className="h-3 w-[92%] rounded" style={{ backgroundColor: skeletonTone.soft }} />
          <Skeleton className="h-3 w-[68%] rounded" style={{ backgroundColor: skeletonTone.soft }} />
        </div>
        <div className="mt-auto flex shrink-0 items-center gap-1.5 border-t pt-1.5" style={{ borderColor: "rgba(31,39,83,0.035)" }}>
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <Skeleton className="h-5 min-w-0 flex-1 rounded-full" style={{ backgroundColor: skeletonTone.soft }} />
            <Skeleton className="h-5 w-6 shrink-0 rounded-full" style={{ backgroundColor: skeletonTone.soft }} />
          </div>
          <div className="hidden shrink-0 gap-1 md:flex">
            <Skeleton className="h-6 w-6 rounded-md" style={{ backgroundColor: skeletonTone.panel }} />
            <Skeleton className="h-6 w-6 rounded-md" style={{ backgroundColor: skeletonTone.panel }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function WorkbenchClipsGridSkeleton({
  count = 9,
  progress,
  showProgress = false,
  showToolbar = true,
  cardSize,
  gridTemplateColumns,
  progressLabel = "Loading videos",
}: {
  count?: number;
  progress?: number;
  showProgress?: boolean;
  showToolbar?: boolean;
  cardSize?: "list" | "small" | "medium" | "large";
  gridTemplateColumns?: string;
  progressLabel?: string;
}) {
  const simulated = useSimulatedLoadingPercent(showProgress && progress === undefined);
  const displayPercent = progress ?? simulated;

  return (
    <div className="ds-studio-skeleton">
      {showProgress ? (
        <LoadingProgressBar percent={displayPercent} label={progressLabel} />
      ) : null}
      {showToolbar ? <div className="mb-1 flex h-9 items-center justify-between gap-2 py-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <Skeleton className="h-3 w-32 rounded max-md:w-24" style={{ backgroundColor: skeletonTone.soft }} />
          <Skeleton className="h-4 w-12 rounded" style={{ backgroundColor: skeletonTone.soft }} />
          <Skeleton className="h-4 w-12 rounded" style={{ backgroundColor: skeletonTone.soft }} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Skeleton className="h-7 w-16 rounded-md max-md:w-7" style={{ backgroundColor: skeletonTone.panel }} />
          <Skeleton className="h-8 w-8 rounded-full md:hidden" style={{ backgroundColor: skeletonTone.panel }} />
        </div>
      </div> : null}
      <div
        className="grid grid-cols-1 items-stretch gap-2 md:[grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr))]"
        style={gridTemplateColumns ? { gridTemplateColumns } : undefined}
      >
        {Array.from({ length: count }).map((_, index) => (
          <WorkbenchClipCardSkeleton key={index} cardSize={cardSize} />
        ))}
      </div>
    </div>
  );
}

export function DatasetStudioPageSkeleton({
  variant = "listing",
  curatedCatalog = false,
  showOwnDatasets = false,
}: {
  variant?: "listing" | "detail";
  curatedCatalog?: boolean;
  showOwnDatasets?: boolean;
}) {
  const progress = useSimulatedLoadingPercent(true);
  return (
    <div
      className={cn(
        "ds-studio-skeleton flex h-[100dvh] flex-col overflow-hidden max-lg:h-[calc(100dvh-3rem)] max-lg:-mb-24",
        variant === "listing" && "lg:h-auto lg:min-h-[100dvh] lg:overflow-visible",
      )}
      style={{ background: CREAM.bg }}
    >
      <LoadingProgressBar percent={progress} label="Loading dataset" />
      {variant === "listing" ? <ListingHeaderSkeleton /> : <DetailHeaderSkeleton />}
      <div className={cn("min-h-0 flex-1 overflow-hidden", variant === "listing" && "lg:overflow-visible")}>
        {variant === "listing" ? (
          <div className={cn(PAGE_CONTAINER, "flex h-full flex-col gap-4 overflow-y-auto pb-10 pt-5 max-lg:pb-28 max-md:px-4 lg:overflow-visible")}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 gap-1.5 overflow-hidden rounded-[18px] p-[3px]" style={{ backgroundColor: "var(--studio-skeleton-listing-pill, #F4F0E8)" }} aria-hidden="true">
                {(curatedCatalog ? [...(showOwnDatasets ? [132] : []), 120, 112, 224] : [68, 132, 120, 112, 224]).map((width, index) => (
                  <Skeleton key={index} className="h-[38px] w-[38px] shrink-0 rounded-[9px] md:h-8 md:w-[var(--tab-width)] md:rounded-[14px]" style={{ "--tab-width": `${width}px`, backgroundColor: index === 0 ? skeletonTone.strong : skeletonTone.soft } as CSSProperties} />
                ))}
              </div>
            </div>
            <StudioDatasetTableSkeleton count={curatedCatalog ? 2 : 12} />
          </div>
        ) : (
          <DetailBodySkeleton />
        )}
      </div>
    </div>
  );
}

/** Full-app skeleton: left nav rail + main Dataset Studio surface (used while the route chunk loads). */
export function DatasetStudioFullPageSkeleton({
  variant = "listing",
  curatedCatalog = false,
  showOwnDatasets = false,
}: {
  variant?: "listing" | "detail";
  curatedCatalog?: boolean;
  showOwnDatasets?: boolean;
}) {
  return (
    <div className="ds-studio-skeleton min-h-[100dvh] bg-[#FCFAF4]">
      <MobileTopBarSkeleton />
      <SidebarNavSkeleton />
      <div className="lg:pl-16">
        <DatasetStudioPageSkeleton variant={variant} curatedCatalog={curatedCatalog} showOwnDatasets={showOwnDatasets} />
      </div>
    </div>
  );
}

/** True when the pathname is a Dataset Studio route (with or without /live-demo). */
export function isDatasetStudioPath(pathname: string): boolean {
  const path = pathname.startsWith("/live-demo")
    ? pathname.slice("/live-demo".length) || "/"
    : pathname;
  return path === "/dataset-studio" || path.startsWith("/dataset-studio/");
}

export function datasetStudioSkeletonVariant(
  pathname: string,
): "listing" | "detail" {
  const path = pathname.startsWith("/live-demo")
    ? pathname.slice("/live-demo".length) || "/"
    : pathname;
  return path === "/dataset-studio" ? "listing" : "detail";
}
